import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Formik, Form } from 'formik'
import { json2csv } from 'json-2-csv'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'
import ConfirmModal from 'components/Modal/ConfirmModal'

import { exportDomainMessages } from './messages'
import { fetchDomainExportThunk } from 'features/domains/slice'
import type { DomainType } from 'features/domains/types'
import { selectDomainExport } from 'features/rootDomains/selectors'
import FileSaver from 'file-saver'

interface ExportDomainModalProps {
  domain: DomainType
  onClose: () => void
}

const ExportDomainModal = ({ domain, onClose }: ExportDomainModalProps) => {
  const dispatch = useAppDispatch()
  const domainExport = useAppSelector(selectDomainExport.data)
  const [isExportLoaded, setIsExportLoaded] = useState(false)
  const isFinishedExportDomains = useAppSelector(selectDomainExport.isFinished)

  const initialValues = {
    domainName: domain.name,
  }

  useEffect(() => {
    if (!isExportLoaded) {
      dispatch(fetchDomainExportThunk(domain.id))
    }
    if (isFinishedExportDomains) {
      setIsExportLoaded(true)
    }
  }, [dispatch, domain, domainExport, isExportLoaded, isFinishedExportDomains])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async ({ domainName }, formProps) => {
        try {
          const fileName = `${domainName}.csv`

          const headers = {
            home_id: 'Savant Home ID',
            home_name: 'Home Name',
            job_id: 'Job Id',
            job_name: 'Job Name',
            domain_path: 'Job Path',
            home_created: 'Home Created Date',
            contact_name: 'Contact Name',
            contact_role: 'Contact Role',
            contact_phone: 'Contact Phone',
            contact_email: 'Contact Email',
            longitude: 'Longitude',
            latitude: 'Latitude',
            address1: 'Address 1',
            address2: 'Address 2',
            city: 'City',
            state: 'State',
            country: 'Country',
            postal_code: 'Posal Code',
            software_version: 'System Version',
            software_date: 'Software Install Date',
            sub_network: 'Network Subscription',
            sub_essentials: 'Essentials Subscription ',
            sub_renewal_date: 'Subscription Renewal Date',
            sw_tier_id: 'Software Tier ID',
            machine_type: 'Machine Type',
            model_number: 'Host Model',
            part_number: 'Part Number',
            mac_os: 'macOS',
            hw_tier_name: 'Host Type',
            uid: 'Host UID',
            imsi: 'IMSI',
            zones_total: 'Total Zones',
            zones_av: 'AV Zones',
            has_lighting: 'Lighting',
            has_shades: 'Automated Shades',
            has_hvac: 'Climate',
            has_cameras: 'Cameras',
            has_security: 'Security',
            has_power: 'Battery Storage',
          }

          if (isFinishedExportDomains && domainExport !== null) {
            let newArr = JSON.parse(JSON.stringify(domainExport))
            newArr.unshift(headers)

            for (let i = 0; i < newArr.length; i++) {
              newArr[i].home_created = new Date(
                newArr[i].home_created
              ).toString()
              newArr[i].software_date = new Date(
                newArr[i].software_date
              ).toString()
            }

            const blobPart = json2csv(newArr, { prependHeader: false })
            const blob = new Blob([blobPart], {
              type: 'text/plain;charset=utf-8',
            })
            FileSaver.saveAs(blob, fileName)
          }

          onClose()
        } catch (err: any) {
          console.error('Error:', err)
        }
      }}
    >
      {(formikProps) => {
        return (
          <ConfirmModal
            isOpen
            onDiscard={onClose}
            title={
              <FormattedMessage
                {...exportDomainMessages.title}
                values={{
                  domainName: domain.name,
                }}
              />
            }
            discardBtnTitle={<FormattedMessage {...commonMessages.cancel} />}
            confirmBtnTitle={
              <FormattedMessage {...exportDomainMessages.download} />
            }
            onConfirm={formikProps.submitForm}
            variant="primary"
            isConfirmBtnDisabled={
              formikProps.isSubmitting || !isFinishedExportDomains
            }
          >
            <Form></Form>
          </ConfirmModal>
        )
      }}
    </Formik>
  )
}

export default ExportDomainModal
